import {action, computed, makeObservable, observable} from 'mobx';
import I18n from 'app/i18n';
import type UserInterface from 'app/interfaces/UserInterface';
import type {ConfigStore} from 'app/store/ConfigStore';
import type {UserStore} from 'app/store/UserStore';

export interface CupUserTokenPayload {
    userToken: string
    nickname?: string
    countryCode?: string
    email?: string
    balance?: string
    currency?: string
    steamId64?: string | null
}

export class CupStore {
    @observable refreshedUserTokenPayload: CupUserTokenPayload | null = null;

    @observable isUserTokenRefreshing = false;

    constructor(
        private readonly userStore: UserStore,
        private readonly configStore: ConfigStore
    ) {
        makeObservable(this);
    }

    @computed
    get user(): UserInterface {
        return this.userStore.user;
    }

    @computed
    get currentExternalId(): string {
        return String(this.user?.id || '');
    }

    @computed
    get nickname(): string {
        return this.refreshedUserTokenPayload?.nickname ??
            this.configStore.CUP_CONFIG.nickname ??
            this.user?.name ??
            '';
    }

    @computed
    get countryCode(): string {
        return this.refreshedUserTokenPayload?.countryCode ??
            this.configStore.CUP_CONFIG.countryCode ??
            this.user?.country_code ??
            '';
    }

    @computed
    get email(): string {
        return this.refreshedUserTokenPayload?.email ??
            this.configStore.CUP_CONFIG.email ??
            '';
    }

    @computed
    get steamId64(): string {
        return this.user?.steam_account.steam_id ?? '';
    }

    @computed
    get balance(): string {
        return this.refreshedUserTokenPayload?.balance ??
            this.configStore.CUP_CONFIG.balance ??
            '';
    }

    @computed
    get currency(): string {
        return this.refreshedUserTokenPayload?.currency ??
            this.configStore.CUP_CONFIG.currency ??
            '';
    }

    @computed
    get timezone(): string {
        return this.configStore.CUP_CONFIG.timezone ?? '';
    }

    @computed
    get parentOrigin(): string {
        return this.configStore.CUP_CONFIG.parentOrigin || '';
    }

    @computed
    get userToken(): string {
        return this.refreshedUserTokenPayload?.userToken ?? this.configStore.CUP_CONFIG.userToken ?? '';
    }

    @computed
    get lang(): string {
        return this.configStore.CUP_CONFIG.lang || I18n.locale;
    }

    @computed
    get lobbyUrl(): string {
        return this.configStore.CUP_LOBBY_URL;
    }

    @computed
    get lobbyOrigin(): string {
        return this.configStore.CUP_ORIGIN;
    }

    @computed
    get centrifugoPath(): string {
        return this.configStore.CUP_CENTRIFUGO_PATH;
    }

    @action
    updateUserTokenPayload(payload: CupUserTokenPayload): void {
        this.refreshedUserTokenPayload = payload;
    }

    @action
    setUserTokenRefreshing(isUserTokenRefreshing: boolean): void {
        this.isUserTokenRefreshing = isUserTokenRefreshing;
    }
}
